import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MediaCard from "@/components/feature/MediaCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import PlayModal from "@/components/feature/PlayModal";
import { getMediaById, getRelated } from "@/utils/media";
import { useSeo } from "@/hooks/useSeo";
import { useFavorites } from "@/hooks/useFavorites";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const media = getMediaById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [playOpen, setPlayOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useSeo({
    title: media
      ? `${media.title} - ${media.type === "movie" ? "电影" : "电视剧"}详情 - 飘花电影网`
      : "影视详情 - 飘花电影网",
    description: media
      ? `${media.title}（${media.year}，${media.region}，${media.genres.join("、")}）：${media.synopsis.slice(0, 60)}...`
      : "飘花电影网影视详情页",
    keywords: media ? `${media.title},${media.genres.join(",")},飘花电影网` : "影视详情,飘花电影网",
  });

  if (!media) {
    return (
      <div className="flex min-h-screen flex-col bg-background-100">
        <SiteHeader />
        <main className="flex flex-1 flex-col items-center justify-center px-4 py-20 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-400">
            <i className="ri-error-warning-line text-3xl" />
          </span>
          <h1 className="mt-4 font-heading text-xl font-bold text-foreground-900">未找到该影视内容</h1>
          <p className="mt-2 text-sm text-foreground-500">内容可能已下架或链接有误，请返回列表重新选择</p>
          <div className="mt-6 flex gap-3">
            <Link to="/movie" className="whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-700">
              浏览电影
            </Link>
            <Link to="/" className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 hover:border-primary-400">
              返回首页
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const fav = isFavorite(media.id);
  const related = getRelated(media, 6);
  const listPath = media.type === "movie" ? "/movie" : "/tv";

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      /* clipboard may be unavailable */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-100">
      <SiteHeader />

      <main className="mx-auto w-full max-w-[1200px] flex-1 px-4 py-6 md:px-6">
        <Breadcrumb items={[{ label: media.type === "movie" ? "电影" : "电视剧", path: listPath }, { label: media.title }]} />

        <div className="mt-4 overflow-hidden rounded-xl border border-background-200 bg-background-50">
          <div className="relative bg-primary-900 px-4 py-6 md:px-8 md:py-8">
            <div className="absolute inset-0 opacity-40">
              <img src={media.poster} alt="" aria-hidden="true" className="h-full w-full object-cover blur-2xl" />
            </div>
            <div className="relative flex flex-col gap-6 md:flex-row">
              <div className="mx-auto w-40 shrink-0 overflow-hidden rounded-lg border-2 border-background-50/20 md:mx-0 md:w-52" style={{ aspectRatio: "2 / 3" }}>
                <img src={media.poster} alt={`${media.title}海报`} title={`${media.title}详情海报`} className="h-full w-full object-top" />
              </div>

              <div className="flex-1 text-background-50">
                <h1 className="font-heading text-2xl font-bold md:text-3xl">{media.title}</h1>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
                  <span className="flex items-center gap-1 rounded bg-accent-500 px-2 py-0.5 font-bold text-primary-950">
                    <i className="ri-star-fill" />
                    {media.rating.toFixed(1)}
                  </span>
                  {media.genres.map((g) => (
                    <span key={g} className="rounded border border-background-50/30 px-2 py-0.5 text-background-100">
                      {g}
                    </span>
                  ))}
                </div>

                <dl className="mt-4 space-y-2 text-sm text-background-200/90">
                  <div className="flex gap-2">
                    <dt className="shrink-0 text-background-300/70">类型：</dt>
                    <dd>{media.type === "movie" ? "电影" : `电视剧（共 ${media.episodes} 集）`}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="shrink-0 text-background-300/70">地区 / 年份：</dt>
                    <dd>{media.region} · {media.year}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="shrink-0 text-background-300/70">导演：</dt>
                    <dd>{media.director}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="shrink-0 text-background-300/70">主演：</dt>
                    <dd>{media.cast.join(" / ")}</dd>
                  </div>
                  {media.duration && (
                    <div className="flex gap-2">
                      <dt className="shrink-0 text-background-300/70">片长：</dt>
                      <dd>{media.duration}</dd>
                    </div>
                  )}
                </dl>

                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setPlayOpen(true)}
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
                  >
                    <i className="ri-play-fill text-base" />
                    立即播放
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleFavorite(media.id)}
                    className={`flex items-center gap-1.5 whitespace-nowrap rounded-md border px-5 py-2.5 text-sm font-medium transition-colors ${
                      fav
                        ? "border-accent-400 bg-accent-500/15 text-accent-400"
                        : "border-background-50/30 text-background-50 hover:bg-background-50/10"
                    }`}
                  >
                    <i className={fav ? "ri-heart-fill" : "ri-heart-line"} />
                    {fav ? "已收藏" : "收藏"}
                  </button>
                  <button
                    type="button"
                    onClick={handleShare}
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-50/30 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
                  >
                    <i className={copied ? "ri-check-line" : "ri-share-forward-line"} />
                    {copied ? "链接已复制" : "分享"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-background-200 px-4 py-6 md:px-8">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-900">
              <span className="h-5 w-1 rounded-full bg-primary-600" />
              剧情简介
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-[15px]">{media.synopsis}</p>
            <p className="mt-3 flex items-center gap-2 rounded-md bg-secondary-50 px-3 py-2 text-xs text-secondary-800">
              <i className="ri-information-line" />
              本站为演示站点，播放功能暂为模拟展示，真实资源与接口将在后续版本支持。
            </p>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3 rounded-xl border border-background-200 bg-background-50 px-4 py-3 md:px-6">
          <span className="text-sm text-foreground-500">站内导航：</span>
          <Link to={listPath} className="flex items-center gap-1 rounded-md bg-background-100 px-3 py-1.5 text-sm text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-800">
            <i className="ri-arrow-left-line" />
            返回{media.type === "movie" ? "电影" : "电视剧"}列表
          </Link>
          <Link to={`/search?q=${encodeURIComponent(media.genres[0])}`} className="flex items-center gap-1 rounded-md bg-background-100 px-3 py-1.5 text-sm text-foreground-700 transition-colors hover:bg-secondary-100 hover:text-secondary-800">
            <i className="ri-price-tag-3-line" />
            {media.genres[0]}相关
          </Link>
        </div>

        <section className="mt-8">
          <div className="mb-4 flex items-center gap-2 border-b-2 border-background-200 pb-2.5">
            <span className="flex h-6 w-6 items-center justify-center rounded-md bg-accent-500 text-primary-950">
              <i className="ri-magic-line text-sm" />
            </span>
            <h2 className="font-heading text-lg font-bold text-foreground-900">相关推荐</h2>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-6">
            {related.map((item) => (
              <MediaCard key={item.id} media={item} compact />
            ))}
          </div>
        </section>
      </main>

      <PlayModal open={playOpen} title={media.title} onClose={() => setPlayOpen(false)} />
      <SiteFooter />
    </div>
  );
}